import { useState, useRef, useEffect, useMemo } from "react";
import type { UserProfile } from "@/types/user";
import type { Team, TeamMember } from "@/types/task";
import { TbPlus, TbEdit, TbTrash, TbX, TbSearch } from "react-icons/tb";
import * as Label from "@radix-ui/react-label";
import { Button } from "@/components/ui/Button";
import { useTeamStore } from "@/stores/teamStore";

// Extended type for team members with user profile data
interface TeamMemberWithProfile extends TeamMember {
  user_profiles: UserProfile;
}

interface TeamManagerProps {
  teams: Team[];
  teamMembers: TeamMemberWithProfile[];
  users: UserProfile[];
  onCreateTeam: (teamData: {
    name: string;
    description: string;
  }) => Promise<void>;
  onUpdateTeam: (
    teamId: string,
    teamData: { name: string; description: string }
  ) => Promise<void>;
  onDeleteTeam: (teamId: string) => void;
  onAddTeamMember: (
    teamId: string,
    userId: string,
    role?: "admin" | "member"
  ) => Promise<void>;
  onRemoveTeamMember: (teamMemberId: string) => void;
  error?: string | null;
}

export const TeamManager: React.FC<TeamManagerProps> = ({
  teams,
  teamMembers,
  users,
  onCreateTeam,
  onUpdateTeam,
  onDeleteTeam,
  onAddTeamMember,
  onRemoveTeamMember,
  error,
}) => {
  // Zustand store
  const {
    showCreateTeam,
    editingTeam,
    newTeamData,
    validationError,
    setNewTeamData,
    setValidationError,
    startCreateTeam,
    startEditTeam,
    resetTeamForm,
    validateTeamData,
  } = useTeamStore();

  // Local state for search and dropdowns
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>(
    {}
  );
  const [dropdownOpen, setDropdownOpen] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState("");

  const filteredTeams = useMemo(() => {
    if (!searchTerm) return teams;
    return teams.filter((team) =>
      team.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [teams, searchTerm]);
  // Ref to manage click outside for dropdowns
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // Use ref to track current dropdown state without causing effect re-runs
  const dropdownOpenRef = useRef(dropdownOpen);
  dropdownOpenRef.current = dropdownOpen;

  // Handle click outside to close dropdowns - runs once on mount
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const currentDropdownOpen = dropdownOpenRef.current;
      Object.keys(currentDropdownOpen).forEach((teamId) => {
        if (currentDropdownOpen[teamId] && dropdownRefs.current[teamId]) {
          if (!dropdownRefs.current[teamId]?.contains(event.target as Node)) {
            setDropdownOpen((prev) => ({ ...prev, [teamId]: false }));
          }
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getTeamMembers = (teamId: string) => {
    return teamMembers.filter((member) => member.team_id === teamId);
  };

  const handleSubmit = async () => {
    // Validate team data before submission
    if (!validateTeamData()) {
      return; // Stop submission if validation fails
    }

    if (editingTeam) {
      await onUpdateTeam(editingTeam.id, newTeamData);
    } else {
      await onCreateTeam(newTeamData);
    }

    // Reset form state using Zustand
    resetTeamForm();
  };

  const handleEdit = (team: Team) => {
    startEditTeam(team);
  };

  const handleAddMember = async (teamId: string, userId: string) => {
    await onAddTeamMember(teamId, userId);
    // Reset the search query for this team
    setSearchQueries((prev) => ({ ...prev, [teamId]: "" }));
    setDropdownOpen((prev) => ({ ...prev, [teamId]: false }));
  };

  return (
    <div>
      {/* Full Screen Create/Edit Team Form */}
      {showCreateTeam || editingTeam ? (
        <>
          <div className="flex flex-col items-start w-full overflow-y-auto min-h-[400px] max-h-[calc(100vh-380px)] p-6 rounded-[10px] border border-gray-100">
            <div className="space-y-6 mb-8 w-full">
              <div>
                <Label.Root
                  htmlFor="team-name"
                  className="inline-flex gap-1 text-[14px] font-medium text-[#444444] mb-3"
                >
                  Team Name <span className="text-red-500">*</span>
                </Label.Root>
                <div className="bg-gray-100 rounded-[10px] px-4 py-3">
                  <input
                    id="team-name"
                    type="text"
                    value={newTeamData.name}
                    onChange={(e) => {
                      setNewTeamData({
                        ...newTeamData,
                        name: e.target.value,
                      });
                      // Clear validation error when user starts typing
                      if (validationError) {
                        setValidationError(null);
                      }
                    }}
                    className="bg-transparent text-[14px] text-[#444444] font-medium leading-[1.5] placeholder-[#828282] border-none outline-none w-full"
                    placeholder="Enter team name"
                  />
                </div>
                {validationError && (
                  <div className="text-red-500 text-[12px] font-medium mt-1">
                    {validationError}
                  </div>
                )}
              </div>
              <div>
                <Label.Root
                  htmlFor="team-description"
                  className="block text-[14px] font-medium text-[#444444] mb-3"
                >
                  Description
                </Label.Root>
                <div className="bg-gray-100 rounded-[10px] px-4 py-3">
                  <textarea
                    id="team-description"
                    value={newTeamData.description}
                    onChange={(e) =>
                      setNewTeamData({
                        ...newTeamData,
                        description: e.target.value,
                      })
                    }
                    className="bg-transparent text-[14px] text-[#444444] font-medium leading-[1.5] placeholder-[#828282] border-none outline-none w-full resize-none min-h-[80px]"
                    placeholder="Enter team description"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-6 w-full">
            <button
              onClick={handleSubmit}
              className="self-stretch w-full px-6 py-3 rounded-[10px] gap-2.5 text-white bg-[#374795] hover:bg-[#132477] cursor-pointer text-sm font-semibold font-['Open_Sans'] leading-tight"
            >
              {editingTeam ? "Edit Teams" : "Create New Teams"}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-col items-start w-full overflow-y-auto max-h-[calc(100vh-380px)]">
            <div className="space-y-4 w-full">
              {error && (
                <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {error}
                </div>
              )}
              {/* Search Bar */}
              <div className="flex flex-col items-start gap-[6px] w-full rounded-lg">
                <div className="flex p-[13px_14px] items-center gap-4 w-full rounded-[10px] bg-[#F3F4F6]">
                  <div className="flex justify-center items-center gap-2 flex-1">
                    <TbSearch className="w-4 h-4 text-[#828282]" />
                    <input
                      type="text"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="bg-transparent border-none outline-none text-[#828282] font-['Open_Sans'] text-[12px] font-medium leading-[18px] flex-1 placeholder:text-[#828282]"
                    />
                  </div>
                </div>
              </div>
              {/* Teams List */}
              <div className="space-y-4">
                {filteredTeams.map((team) => (
                  <div
                    key={team.id}
                    className="bg-[rgba(55,71,149,0.02)] rounded p-4"
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col gap-1.5">
                        <h4 className="font-bold text-[#444444] text-[16px] leading-[1.4]">
                          {team.name}
                        </h4>
                        <div className="flex items-center gap-2">
                          {team.description && (
                            <>
                              <span className="text-[#828282] text-[12px] leading-[1.3] font-medium">
                                {team.description}
                              </span>
                              <div className="w-[5px] h-[5px] bg-[#828282] rounded-full"></div>
                            </>
                          )}
                          <span className="text-[#828282] text-[12px] leading-[1.3] font-medium">
                            Created:{" "}
                            {new Date(team.created_at).toLocaleDateString(
                              "en-GB"
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(team)}
                          className="w-9 h-9 rounded-full border border-gray-100 bg-white flex items-center cursor-pointer justify-center hover:bg-gray-50 transition-colors"
                          title="Edit Team"
                        >
                          <TbEdit size={14} className="text-[#828282]" />
                        </button>
                        <button
                          onClick={() => onDeleteTeam(team.id)}
                          className="w-9 h-9 rounded-full border border-gray-100 bg-white flex items-center cursor-pointer justify-center hover:bg-gray-50 transition-colors"
                          title="Delete Team"
                        >
                          <TbTrash size={14} className="text-red-400" />
                        </button>
                      </div>
                    </div>

                    {/* Team Members */}
                    <div className="border-t border-gray-200 pt-3 mt-3">
                      <div className="flex justify-between items-center mb-2">
                        <h5 className="font-medium text-[#444444] text-sm">
                          Members ({getTeamMembers(team.id).length})
                        </h5>
                        <div className="flex items-center gap-2">
                          {/* Debug info - remove in production */}
                          {process.env.NODE_ENV === "development" && (
                            <small className="text-xs text-gray-400">
                              Users: {users.length}, Available:{" "}
                              {(() => {
                                const availableUsers = users.filter(
                                  (user) =>
                                    !getTeamMembers(team.id).some(
                                      (member) =>
                                        member.user_profiles?.id === user.id
                                    )
                                );
                                return availableUsers.length;
                              })()}
                            </small>
                          )}

                          {/* Searchable Add Member Dropdown */}
                          <div
                            className="relative"
                            ref={(el) => {
                              dropdownRefs.current[team.id] = el;
                            }}
                          >
                            <div className="relative bg-gray-100 rounded-[10px]">
                              <div className="flex items-center justify-between px-2.5 py-[9px]">
                                <div className="flex items-center gap-2">
                                  <TbSearch
                                    size={16}
                                    className="text-[#828282]"
                                  />
                                  <input
                                    type="text"
                                    placeholder="Search Team"
                                    value={searchQueries[team.id] || ""}
                                    onChange={(e) => {
                                      setSearchQueries((prev) => ({
                                        ...prev,
                                        [team.id]: e.target.value,
                                      }));
                                      setDropdownOpen((prev) => ({
                                        ...prev,
                                        [team.id]: true,
                                      }));
                                    }}
                                    onFocus={() =>
                                      setDropdownOpen((prev) => ({
                                        ...prev,
                                        [team.id]: true,
                                      }))
                                    }
                                    className="bg-transparent text-[12px] text-[#828282] font-medium leading-[1.5] placeholder-[#828282] border-none outline-none min-w-[180px]"
                                  />
                                </div>
                                <TbPlus size={16} className="text-[#828282]" />
                              </div>
                            </div>

                            {/* Custom Dropdown */}
                            {dropdownOpen[team.id] && (
                              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] max-h-[200px] overflow-y-auto">
                                {(() => {
                                  const searchTerm =
                                    searchQueries[team.id]?.toLowerCase() || "";
                                  const availableUsers = users.filter(
                                    (user) =>
                                      !getTeamMembers(team.id).some(
                                        (member) =>
                                          member.user_profiles?.id === user.id
                                      ) &&
                                      user.email
                                        .toLowerCase()
                                        .includes(searchTerm)
                                  );

                                  if (availableUsers.length === 0) {
                                    return (
                                      <div className="text-[12px] px-3 py-3 text-[#828282] italic">
                                        {searchTerm
                                          ? "No users found matching your search"
                                          : "No available users to add"}
                                      </div>
                                    );
                                  }

                                  return availableUsers.map((user) => {
                                    const primaryRole =
                                      user.rbac_roles?.[0]?.name || "User";
                                    const isAdminRole = user.rbac_roles?.some(
                                      (role) =>
                                        role.name === "Admin" ||
                                        role.name === "Super Admin"
                                    );

                                    return (
                                      <div
                                        key={user.id}
                                        className="px-3 py-2.5 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                                        onClick={() =>
                                          handleAddMember(team.id, user.id)
                                        }
                                      >
                                        <div className="flex items-center justify-between">
                                          <span className="text-[12px] text-[#444444] font-medium">
                                            {user.email}
                                          </span>
                                          <span
                                            className={`px-2 py-0.5 text-xs rounded-full ${
                                              isAdminRole
                                                ? "bg-purple-100 text-purple-800"
                                                : "bg-gray-100 text-[#828282]"
                                            }`}
                                          >
                                            {primaryRole}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                            )}

                            {/* Click outside to close */}
                            {dropdownOpen[team.id] && (
                              <div
                                className="fixed inset-0 z-[9998]"
                                onClick={() =>
                                  setDropdownOpen((prev) => ({
                                    ...prev,
                                    [team.id]: false,
                                  }))
                                }
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        {getTeamMembers(team.id).map((member) => {
                          const primaryRole =
                            member.user_profiles?.rbac_roles?.[0]?.name ||
                            "User";
                          const isAdminRole =
                            member.user_profiles?.rbac_roles?.some(
                              (role) =>
                                role.name === "Admin" ||
                                role.name === "Super Admin"
                            );

                          return (
                            <div
                              key={member.id}
                              className="flex justify-between items-center py-2 px-3 bg-white rounded border border-gray-100"
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-[#444444]">
                                  {member.user_profiles?.email ||
                                    "Unknown User"}
                                </span>
                                <span
                                  className={`px-2 py-0.5 text-xs rounded-full justify-start text-[8px] font-bold uppercase leading-3 tracking-tight ${
                                    isAdminRole
                                      ? "bg-purple-100 text-purple-800"
                                      : "bg-gray-100 text-indigo-800"
                                  }`}
                                >
                                  {primaryRole}
                                </span>
                              </div>
                              <button
                                onClick={() => onRemoveTeamMember(member.id)}
                                className="w-6 h-6 rounded-full border border-gray-200 bg-white flex items-center justify-center hover:bg-red-50 hover:border-red-200 transition-colors"
                                title="Remove Member"
                              >
                                <TbX
                                  size={12}
                                  className="text-[#828282] hover:text-red-600"
                                />
                              </button>
                            </div>
                          );
                        })}
                        {getTeamMembers(team.id).length === 0 && (
                          <p className="text-sm text-[#828282] py-2 italic">
                            No members yet
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {teams.length === 0 && (
                  <div className="text-center py-8 text-[#828282]">
                    No teams created yet. Create your first team!
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-center items-center mt-1">
            <Button
              onClick={startCreateTeam}
              variant="primary"
              className="self-stretch w-full px-6 py-3 rounded-[10px] inline-flex items-center gap-2.5 text-white bg-[#374795] hover:bg-[#132477] cursor-pointer text-sm font-semibold font-['Open_Sans'] leading-tight"
            >
              <TbPlus size={16} />
              Create New Teams
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
